import { AlertTriangle } from 'lucide-react';
import { formatDateTime } from '@/lib/dateFormat';
import type { AssetDefender } from '@/types/oscar';
import { defenderBadge, onOff, overdue } from './defenderFormat';

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="font-semibold text-darkGrey/70">{label}</dt>
      <dd className="break-words">{value || '—'}</dd>
    </>
  );
}

/** The label under a Windows device's name. Nothing is rendered when Intune has nothing for the device. */
export function DefenderBadges({ defender }: { defender: AssetDefender }) {
  const b = defenderBadge(defender);
  return (
    <ul aria-label="Defender" className="mt-1 flex flex-wrap gap-2">
      <li
        title={defender.HealthReasons ?? undefined}
        className={b.tone === 'ok'
          ? 'rounded-full border border-navy/30 bg-navy/5 px-2.5 py-0.5 text-xs font-semibold text-navy'
          : b.tone === 'warn'
            ? 'rounded-full border border-orange/40 bg-orange/10 px-2.5 py-0.5 text-xs font-semibold text-navy'
            : 'rounded-full border border-border bg-bg px-2.5 py-0.5 text-xs font-semibold text-darkGrey'}
      >
        {b.text}
      </li>
    </ul>
  );
}

/**
 * What Intune says about Microsoft Defender on a Windows device, read-only.
 *
 * <p>⚠ THIS IS INTUNE'S VIEW. It says whether protection is on, whether the signatures are current and whether
 * Defender is waiting on something. It has no risk score and no exposure level: that is the Defender portal, and
 * the page says so rather than letting "healthy" read as "safe".</p>
 *
 * <p>⚠ Nothing here is edited by hand: the Defender sync overwrites it every six hours.</p>
 */
export function AssetDefenderTab({ defender }: { defender: AssetDefender }) {
  return (
    <div className="space-y-3">
      {defender.Health === 'attention' && defender.HealthReasons && (
        <p role="alert" className="flex items-start gap-2 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span><strong className="font-semibold text-navy">Needs attention:</strong> {defender.HealthReasons}.</span>
        </p>
      )}
      {!defender.Reported && (
        <p className="rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey">
          Intune has no Defender report for this device. That is not the same as healthy: the device may not have
          checked in, or Defender may not be reporting to Intune.
        </p>
      )}

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded border border-border bg-white p-3 text-sm text-darkGrey">
        <Row label="Malware protection" value={onOff(defender.MalwareProtectionEnabled)} />
        <Row label="Real-time protection" value={onOff(defender.RealTimeProtectionEnabled)} />
        <Row label="Network inspection" value={onOff(defender.NetworkInspectionEnabled)} />
        <Row label="Tamper protection" value={onOff(defender.TamperProtectionEnabled)} />
        <Row label="Signatures" value={overdue(defender.SignatureUpdateOverdue)} />
        <Row label="Quick scan" value={`${overdue(defender.QuickScanOverdue)}${defender.LastQuickScan ? ` (last ${formatDateTime(defender.LastQuickScan)})` : ''}`} />
        <Row label="Full scan" value={`${overdue(defender.FullScanOverdue)}${defender.LastFullScan ? ` (last ${formatDateTime(defender.LastFullScan)})` : ''}`} />
        <Row label="Defender state" value={defender.DeviceState} />
        <Row label="Restart required" value={defender.RebootRequired === null ? 'Not reported' : (defender.RebootRequired ? 'Yes' : 'No')} />
        <Row label="Signature version" value={defender.SignatureVersion} />
        <Row label="Engine version" value={defender.EngineVersion} />
        <Row label="Antimalware client" value={defender.AntiMalwareVersion} />
        <Row label="Device last reported" value={defender.LastReported ? formatDateTime(defender.LastReported) : null} />
        <Row label="Last synced from Intune" value={formatDateTime(defender.LastSyncDate)} />
      </dl>

      <p className="text-xs text-darkGrey/70">
        This is what Intune records about Defender on the device. It is not a risk score or exposure level: read those in the
        Defender portal.
      </p>
      <p className="text-xs text-darkGrey/60">Read from Intune and refreshed every six hours. It is not edited here.</p>
    </div>
  );
}
